import { ArrowLeftOutlined, CheckOutlined, CopyOutlined, DeleteOutlined, InfoCircleOutlined, PrinterOutlined } from '@ant-design/icons';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import api from '~/services/api';
import history from '~/services/history';

import { Container, HeaderCard } from './styles';

const { Text } = Typography;

const getStatusColor = (status) => {
  const colors = {
    pending: 'orange',
    in_progress: 'blue',
    complete: 'green',
  };
  return colors[status] || 'default';
};

const getStatusText = (status) => {
  const texts = {
    pending: 'Pending',
    in_progress: 'In Progress',
    complete: 'Complete',
  };
  return texts[status] || status;
};

export default function InternalOrderEdit({ location }) {
  const [form] = Form.useForm();
  const [formOrderDetail] = Form.useForm();
  const [cloneForm] = Form.useForm();

  const { TextArea } = Input;

  const profile = useSelector((state) => state.user.profile);

  const [loading, setLoading] = useState(true);

  const [orderInfoData, setOrderInfoData] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [orderChecklistItems, setOrderChecklistItems] = useState([]);
  const [orderItemsOptions, setOrderItemsOptions] = useState([]);
  const [technicianList, setTechnicianList] = useState({});
  const [userList, setUserList] = useState([]);

  const [orderStatus, setOrderStatus] = useState('');
  const [internalOrderId, setInternalOrderId] = useState('');
  const [cloneModalVisible, setCloneModalVisible] = useState(false);
  const [cloneLoading, setCloneLoading] = useState(false);
  const [loadingAmazonLabels, setLoadingAmazonLabels] = useState(false);
  const [amazonLabelsModalVisible, setAmazonLabelsModalVisible] = useState(false);
  const [amazonLabelSerials, setAmazonLabelSerials] = useState([]);
  const [selectedAmazonLabelIndexes, setSelectedAmazonLabelIndexes] = useState([]);
  const [lastAmazonLabelClickedIndex, setLastAmazonLabelClickedIndex] = useState(null);

  const serialNumberRef = useRef('');
  const notesRef = useRef('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const orderId = params.get('internal_order_id');
    setInternalOrderId(orderId);

    async function loadDropdownboxes() {
      const internalOrderInfo = await api.get(`internal_orders/${orderId}`);
      const orderChecklistItemsList = await api.get(`internal_order_checklist/${orderId}`);

      formOrderDetail.setFieldsValue({
        requested_by_id: internalOrderInfo.data.requested_by_id,
        technician_id: internalOrderInfo.data.technician_id,
        notes: internalOrderInfo.data.notes,
      });

      setOrderStatus(internalOrderInfo.data.status);
      setOrderInfoData(internalOrderInfo.data);

      setOrderItems(internalOrderInfo.data.internal_order_items);
      setOrderItemsOptions(internalOrderInfo.data.internal_order_items.map((item) => ({ label: item.title, value: item.id })));

      const { data: technicianData } = await api.get('technicians?list=true');
      const technicians = technicianData?.map((technician) => ({ value: technician.id, label: technician.name }));
      setTechnicianList(technicians);

      const { data: userData } = await api.get('users');
      const list = userData?.map((user) => ({ value: user.id, label: user.name }));
      setUserList(list);

      const newData = orderChecklistItemsList.data.map(async (ioItem) => {
        const storage = [ioItem.inventory_item?.storage, ioItem.inventory_item?.storage_2].filter((element) => element).join(' / ');
        const ram = [ioItem.inventory_item?.ram, ioItem.inventory_item?.ram_2].filter((element) => element).join(' / ');
        const battery = [
          ioItem.inventory_item?.battery_item?.part_number,
          ioItem.inventory_item?.battery_item2?.part_number,
          ioItem.inventory_item?.battery_item3?.part_number,
        ]
          .filter((element) => element)
          .join(' / ');
        return {
          index: ioItem.id,
          notes: ioItem.notes,
          partNumber: ioItem.inventory_item.item.part_number,
          item: ioItem.inventory_item.item.name,
          itemId: ioItem.inventory_item.item.id,
          storage,
          ram,
          battery,
          serialNumber: ioItem.inventory_item.serial_number,
          inventoryItemId: ioItem.inventory_items_id,
          technician: ioItem.technician.name,
          isProcessed: ioItem.inventory_item.is_processed,
        };
      });

      const results = await Promise.all(newData);
      setOrderChecklistItems(results);

      setLoading(false);
    }
    loadDropdownboxes();
  }, []); //eslint-disable-line

  const handleRemoveChecklistItem = useCallback(
    async (rowInfo) => {
      try {
        const { index, serialNumber } = rowInfo;

        const getAssetInfo = await api.post('assetDiagnostic/showBySerialNumber', { serialNumber });

        await api.put('inventory_items', {
          items: [
            {
              serial_number: serialNumber,
              asset_diagnostic_id: getAssetInfo.data.id,
              action: 'removedFromInternalOrder',
              orderNumber: orderInfoData.internal_order_number,
            },
          ],
        });

        await api.delete(`internal_order_checklist/${index}`);
        setOrderChecklistItems((prev) => prev.filter((el) => el.index !== index));
        toast.success('Item removed successfully and is back in stock');
      } catch (error) {
        toast.error('Could not remove this item. Contact RBMS admin');
      }
    },
    [orderInfoData.internal_order_number]
  );

  const onFinish = useCallback(
    async (values) => {
      const { checklistNotes, checklistOrderItem, checklistSerialNumber } = values;

      const serialNumberFormatted =
        checklistSerialNumber.substring(0, 4) === 'P/N:'
          ? checklistSerialNumber
              .substring(checklistSerialNumber.indexOf('S/N:') + 4)
              .trim()
              .toUpperCase()
          : checklistSerialNumber.trim().toUpperCase();

      try {
        /**
         * Adding item using SERIAL number
         */
        if (serialNumberFormatted !== undefined && serialNumberFormatted !== '') {
          const getItemInfo = await api.post('inventory_items/showBySerialNumber', { serial_number: serialNumberFormatted });

          if (getItemInfo.data === null || getItemInfo.data?.length === 0) {
            serialNumberRef.current.focus();
            throw new Error('Serial number not found');
          }

          if (getItemInfo.data.status === 'Downstream') {
            serialNumberRef.current.focus();
            throw new Error('This serial number status is Downstream and cannot be added to an order');
          }

          if (getItemInfo.data.status === 'Sold') {
            serialNumberRef.current.focus();
            throw new Error('This serial number is Sold');
          }

          if (getItemInfo.data.status === 'AwaitingShipment') {
            serialNumberRef.current.focus();
            throw new Error('This serial number is assigned to another order');
          }

          await api.put('inventory_items', {
            items: [
              {
                serial_number: serialNumberFormatted,
                action: 'addedToInternalOrder',
                orderNumber: orderInfoData.internal_order_number,
              },
            ],
          });

          const newOrderChecklistItem = await api.post('internal_order_checklist', {
            internal_order_items_id: checklistOrderItem,
            technician_id: profile.technician_id,
            inventory_items_id: getItemInfo.data.id,
            internal_order_id: Number(internalOrderId),
            notes: checklistNotes,
          });

          const newChecklist = {
            index: newOrderChecklistItem.data.id,
            isProcessed: getItemInfo.data.is_processed,
            serialNumber: serialNumberFormatted,
            technician: profile.name,
            partNumber: getItemInfo.data.item.part_number,
            item: getItemInfo.data.item.name,
            itemId: getItemInfo.data.item.id,
            notes: checklistNotes,
          };

          if (orderChecklistItems.length === 0) {
            await api.put(`internal_orders/${internalOrderId}`, { status: 'in_progress' });
          }

          setOrderChecklistItems([...orderChecklistItems, newChecklist]);
          form.setFieldsValue({ checklistSerialNumber: '' });
          serialNumberRef.current.focus();
          toast.success('Item registered successfully');
        }
      } catch (error) {
        toast.error(error.message);
      }
      form.setFieldsValue({ checklistNotes: '' });
      return '';
    },
    [form, internalOrderId, orderChecklistItems, orderInfoData.internal_order_number, profile]
  );
  const handleComplete = useCallback(async () => {
    if (orderChecklistItems.length === 0) {
      toast.error('No serial number was added.');
      return;
    }

    const result = await api.put(`internal_orders/${internalOrderId}`, { status: 'complete' });

    if (result.data.error) {
      toast.error(result.data.error);
      return;
    }

    toast.success('Order completed successfully');
    history.push('/internalOrders');
  }, [orderChecklistItems.length, internalOrderId]);

  const profileType = profile?.type;
  const orderInfoId = orderInfoData.id;

  const columnsChecklist = useMemo(
    () => [
      {
        title: '#',
        dataIndex: 'index',
        width: 40,
        render: (_text, _row, index) => index + 1,
      },
      {
        title: 'Serial Number',
        dataIndex: 'serialNumber',
        // width: 150,
      },
      {
        title: 'Part Number',
        dataIndex: 'partNumber',
        // width: 130,
      },
      {
        title: 'Item',
        dataIndex: 'item',
      },
      {
        title: 'Storage',
        dataIndex: 'storage',
        // width: 100,
      },
      {
        title: 'RAM',
        dataIndex: 'ram',
        // width: 100,
      },
      {
        title: 'Battery',
        dataIndex: 'battery',
        // width: 120,
      },
      {
        title: 'Technician',
        dataIndex: 'technician',
        // width: 120,
      },
      {
        title: 'Inspected',
        dataIndex: 'isProcessed',
        // width: 90,
        align: 'center',
        render: (text, row) =>
          row.serialNumber === null || text ? (
            <ThumbUpIcon style={{ color: '#52c41a', fontSize: 20 }} />
          ) : (
            <ThumbDownIcon style={{ color: '#ff4d4f', fontSize: 20 }} />
          ),
      },
      {
        title: 'Inspection',
        dataIndex: 'inspection',
        // width: 100,
        align: 'center',
        render: (_text, row) =>
          row.serialNumber === null ? (
            <Button type="link" href={row.workInstructionUrl} target="_blank" size="small">
              {row.workInstructionName}
            </Button>
          ) : (
            <Link to={`inventoryProcessing/${row.inventoryItemId}?orderId=${orderInfoId}&orderType=internal`}>
              <Button type="link" size="small">
                Test
              </Button>
            </Link>
          ),
      },
      {
        title: 'Notes',
        dataIndex: 'notes',
        ellipsis: { showTitle: false },
        render: (notes) => (
          <Tooltip placement="topLeft" title={notes}>
            {notes}
          </Tooltip>
        ),
      },
      {
        title: 'Actions',
        dataIndex: 'actions',
        // width: 100,
        fixed: 'right',
        align: 'center',
        render: (_text, record) => {
          const canRemove = orderStatus !== 'complete' || profileType === 999;
          return (
            <Popconfirm
              onConfirm={() => handleRemoveChecklistItem(record)}
              title="Remove this item?"
              disabled={!canRemove}
              placement="left"
              okText="Yes"
              cancelText="No"
            >
              <Button danger size="small" disabled={!canRemove}>
                Remove
              </Button>
            </Popconfirm>
          );
        },
      },
    ],
    [orderStatus, profileType, orderInfoId, handleRemoveChecklistItem]
  );

  const columnsItems = useMemo(
    () => [
      {
        title: 'Item',
        dataIndex: 'title',
      },
      {
        title: 'Qty',
        dataIndex: 'quantity',
        width: 80,
        align: 'center',
      },
      {
        title: 'Item Notes',
        dataIndex: 'notes',
        ellipsis: { showTitle: false },
        render: (notes) => (
          <Tooltip placement="topLeft" title={notes}>
            {notes}
          </Tooltip>
        ),
      },
    ],
    []
  );

  const handleUpdateOrderDetail = useCallback(
    async (values) => {
      const { requested_by_id, technician_id, notes } = values;

      try {
        await api.put(`internal_orders/${internalOrderId}`, {
          requested_by_id,
          technician_id,
          notes,
        });
        toast.success('Order details updated successfully');
      } catch (error) {
        toast.error('Error updating order details');
      }
    },
    [internalOrderId]
  );

  const totalItems = useMemo(() => orderChecklistItems.length, [orderChecklistItems.length]);
  const inspectedItems = useMemo(() => orderChecklistItems.filter((item) => item.isProcessed).length, [orderChecklistItems]);

  const handleCloneOrder = useCallback(
    async (values) => {
      setCloneLoading(true);
      try {
        await api.post('internal_orders/clone', {
          internalOrderId: Number(internalOrderId),
          copies: values.copies,
        });
        toast.success('Order cloned successfully');
        setCloneModalVisible(false);
        cloneForm.resetFields();
        history.push('/internalOrders');
      } catch (error) {
        toast.error(error.response?.data?.message || 'Error cloning order');
      } finally {
        setCloneLoading(false);
      }
    },
    [internalOrderId, cloneForm]
  );

  const availableAmazonSerials = useMemo(
    () =>
      orderChecklistItems
        .filter((item) => Boolean(item?.serialNumber))
        .map((item) => ({ id: item.index, serial: item.serialNumber, partNumber: item?.partNumber || '--', itemName: item?.item || '--' })),
    [orderChecklistItems]
  );

  const openAmazonLabelsModal = useCallback(() => {
    if (availableAmazonSerials.length === 0) {
      toast.error('No serial numbers found in checklist.');
      return;
    }

    setAmazonLabelSerials(availableAmazonSerials);
    setSelectedAmazonLabelIndexes(availableAmazonSerials.map((_serial, index) => index));
    setLastAmazonLabelClickedIndex(null);
    setAmazonLabelsModalVisible(true);
  }, [availableAmazonSerials]);

  const handleAmazonLabelSelectionChange = useCallback(
    (index, checked, isShiftPressed) => {
      setSelectedAmazonLabelIndexes((previousIndexes) => {
        const nextIndexes = new Set(previousIndexes);

        if (isShiftPressed && lastAmazonLabelClickedIndex !== null) {
          const start = Math.min(lastAmazonLabelClickedIndex, index);
          const end = Math.max(lastAmazonLabelClickedIndex, index);

          for (let rangeIndex = start; rangeIndex <= end; rangeIndex += 1) {
            if (checked) {
              nextIndexes.add(rangeIndex);
            } else {
              nextIndexes.delete(rangeIndex);
            }
          }
        } else if (checked) {
          nextIndexes.add(index);
        } else {
          nextIndexes.delete(index);
        }

        return Array.from(nextIndexes).sort((a, b) => a - b);
      });

      setLastAmazonLabelClickedIndex(index);
    },
    [lastAmazonLabelClickedIndex]
  );

  const handlePrintAmazonLabels = useCallback(async () => {
    const serials = selectedAmazonLabelIndexes.map((index) => amazonLabelSerials[index]?.serial).filter(Boolean);

    if (serials.length === 0) {
      toast.error('Select at least one serial number.');
      return;
    }

    try {
      setLoadingAmazonLabels(true);
      await api.post('http://127.0.0.1:17891/amazon-inventory-print-serials', serials);
      toast.success('Amazon labels sent to printer.');
      setAmazonLabelsModalVisible(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not print Amazon labels.');
    } finally {
      setLoadingAmazonLabels(false);
    }
  }, [amazonLabelSerials, selectedAmazonLabelIndexes]);

  return (
    <Container>
      <HeaderCard>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Space size="large">
              <Link to="/internalOrders">
                <Button type="default" icon={<ArrowLeftOutlined />} size="large">
                  Back
                </Button>
              </Link>
              <Button type="default" icon={<CopyOutlined />} size="large" onClick={() => setCloneModalVisible(true)}>
                Clone this order
              </Button>
              <Button
                type="default"
                icon={<PrinterOutlined />}
                size="large"
                onClick={openAmazonLabelsModal}
                loading={loadingAmazonLabels}
                disabled={availableAmazonSerials.length === 0}
              >
                Print Amazon Labels
              </Button>
              <Popconfirm
                title="Are you sure you want to delete this order?"
                onConfirm={async () => {
                  try {
                    await api.delete(`/internal_orders/${internalOrderId}`);
                    toast.success('Order deleted successfully');
                    history.push('/internalOrders');
                  } catch (err) {
                    const msg = err.response?.data?.error || 'Could not delete order.';
                    toast.error(msg);
                  }
                }}
                okText="Yes"
                cancelText="No"
                placement="bottomRight"
              >
                <Button danger type="default" icon={<DeleteOutlined />} size="large">
                  Delete Order
                </Button>
              </Popconfirm>
            </Space>
          </Col>
          <Col>
            <Space>
              <Tag color={getStatusColor(orderStatus)} style={{ fontSize: 14, padding: '4px 12px' }}>
                {getStatusText(orderStatus)}
              </Tag>

              <Popconfirm
                disabled={orderStatus === 'complete' || totalItems === 0}
                onConfirm={handleComplete}
                title="Complete this order?"
                description="This action will mark the order as complete."
                placement="bottomRight"
                okText="Yes"
                cancelText="No"
              >
                <Button type="primary" icon={<CheckOutlined />} size="large" disabled={orderStatus === 'complete' || totalItems === 0}>
                  Complete Order
                </Button>
              </Popconfirm>
            </Space>
          </Col>
        </Row>

        <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 4 }}>
          <Descriptions.Item label="Order Number">
            <Text strong>{orderInfoData.internal_order_number}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Badge status={orderStatus === 'complete' ? 'success' : 'processing'} text={getStatusText(orderStatus)} />
          </Descriptions.Item>
          <Descriptions.Item label="Total Items">
            <Text strong>{totalItems}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Inspected">
            <Text strong style={{ color: inspectedItems === totalItems && totalItems > 0 ? '#52c41a' : '#faad14' }}>
              {inspectedItems} / {totalItems}
            </Text>
          </Descriptions.Item>
        </Descriptions>
      </HeaderCard>

      <Card
        title={
          <Space>
            <InfoCircleOutlined />
            <span>Order Details</span>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        <Form form={formOrderDetail} onFinish={handleUpdateOrderDetail} layout="vertical">
          <Row gutter={[16, 0]}>
            {userList.length > 0 && (
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="requested_by_id" label="Requested By" rules={[{ required: true, message: 'Required' }]}>
                  <Select options={userList} showSearch placeholder="Select requester" />
                </Form.Item>
              </Col>
            )}
            {technicianList.length > 0 && (
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="technician_id" label="Technician" rules={[{ required: true, message: 'Required' }]}>
                  <Select options={technicianList} showSearch placeholder="Select technician" />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} md={8}>
              <Form.Item name="notes" label="Order Notes">
                <TextArea showCount maxLength={2000} placeholder="Add notes" autoSize={{ minRows: 1, maxRows: 3 }} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button type="primary" htmlType="submit">
                  Update Details
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      <Card title="Order Items" style={{ marginBottom: 24 }}>
        <Table columns={columnsItems} dataSource={orderItems} size="small" pagination={false} rowKey="id" />
      </Card>

      {loading ? (
        <Card>
          <Spin tip="Loading order data...">
            <Alert message="Loading" description="Please wait while we load the order items." type="info" />
          </Spin>
        </Card>
      ) : (
        <>
          <Card title="Add Item to Order" style={{ marginBottom: 24 }}>
            <Form form={form} onFinish={onFinish} layout="vertical">
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item name="checklistOrderItem" label="Order Item" rules={[{ required: true, message: 'Required' }]}>
                    <Select placeholder="Select an item" options={orderItemsOptions} showSearch />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="checklistSerialNumber" label="Serial Number" rules={[{ required: true, message: 'Required' }]}>
                    <Input ref={serialNumberRef} placeholder="Enter serial number" autoComplete="off" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="checklistNotes" label="Notes">
                    <TextArea ref={notesRef} placeholder="Add notes" rows={1} showCount maxLength={500} />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item style={{ marginBottom: 0 }}>
                    {orderStatus !== 'complete' || profile?.type === 999 ? (
                      <Button type="primary" htmlType="submit">
                        Add Item
                      </Button>
                    ) : (
                      <Tooltip title="Order is complete and cannot be edited">
                        <Button type="primary" disabled>
                          Add Item
                        </Button>
                      </Tooltip>
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>

          <Card title={`Checklist Items (${totalItems})`}>
            <Table
              columns={columnsChecklist}
              dataSource={orderChecklistItems}
              size="small"
              pagination={false}
              rowKey={(record) => record.index}
              scroll={{ x: 1200 }}
            />
          </Card>
        </>
      )}

      <Modal
        title="Cloning Internal Order"
        open={cloneModalVisible}
        onCancel={() => {
          setCloneModalVisible(false);
          cloneForm.resetFields();
        }}
        footer={null}
      >
        <Form form={cloneForm} onFinish={handleCloneOrder} layout="vertical">
          <Form.Item
            name="copies"
            label="Number of copies"
            rules={[
              { required: true, message: 'Required' },
              { type: 'number', min: 1, max: 20, message: 'Must be between 1 and 20' },
            ]}
          >
            <InputNumber min={1} max={20} style={{ width: '100%' }} placeholder="Enter number of copies" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setCloneModalVisible(false);
                  cloneForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={cloneLoading}>
                Create Copies
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Print Amazon Labels"
        open={amazonLabelsModalVisible}
        onCancel={() => setAmazonLabelsModalVisible(false)}
        onOk={handlePrintAmazonLabels}
        okText="Print Selected"
        cancelText="Cancel"
        confirmLoading={loadingAmazonLabels}
        okButtonProps={{ disabled: selectedAmazonLabelIndexes.length === 0 }}
      >
        <Space direction="vertical" size={8} style={{ width: '100%', maxHeight: 360, overflowY: 'auto' }}>
          {amazonLabelSerials.map((serialEntry, index) => (
            <Checkbox
              key={String(serialEntry.id)}
              checked={selectedAmazonLabelIndexes.includes(index)}
              onChange={(event) => handleAmazonLabelSelectionChange(index, event.target.checked, Boolean(event.nativeEvent?.shiftKey))}
            >
              <div>
                <div>{serialEntry.serial}</div>
                <Text type="secondary">
                  PN: {serialEntry.partNumber} | Item: {serialEntry.itemName}
                </Text>
              </div>
            </Checkbox>
          ))}
        </Space>
      </Modal>
    </Container>
  );
}
